import { useEffect, useState } from "react";
import axios from "../services/axios";

type Suggestion = {
  areaId?: string | number;
  initials: string;
  status: "idle" | "loading" | "ready" | "unavailable";
};

/** Requires the complete, area-scoped backend contract in docs/backend-batch-initials.md. */
export function useBatchInitialsSuggestion(areaId?: string | number): Suggestion {
  const [suggestion, setSuggestion] = useState<Suggestion>({ initials: "", status: "idle" });

  useEffect(() => {
    if (areaId == null) {
      setSuggestion({ initials: "", status: "idle" });
      return;
    }
    let active = true;
    const controller = new AbortController();
    setSuggestion({ areaId, initials: "", status: "loading" });
    axios.get("/batches/next-initials", { params: { area_id: areaId }, signal: controller.signal })
      .then(({ data }) => {
        if (!active) return;
        if (String(data?.area_id) !== String(areaId) || typeof data?.initials !== "string" || !/^LT\d{3}$/.test(data.initials) || data.initials === "LT000") {
          throw new Error("Invalid batch initials suggestion response");
        }
        setSuggestion({ areaId, initials: data.initials, status: "ready" });
      })
      .catch(() => {
        if (active) setSuggestion({ areaId, initials: "", status: "unavailable" });
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [areaId]);

  // Hide the previous area's suggestion during render, before effect cleanup runs.
  if (areaId == null) return { initials: "", status: "idle" };
  return suggestion.areaId === areaId ? suggestion : { areaId, initials: "", status: "loading" };
}
